"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import Footer from "@/components/marketing/footer";

const steps = [
  {
    num: "01",
    title: "Upload",
    body: "Upload your contract \u2014 PDF, Word, RTF, or even a photograph. File integrity validated on upload. Corrupt files rejected before entering the system.",
    image: "/images/features-hero.webp",
  },
  {
    num: "02",
    title: "Classify",
    body: "AI identifies the agreement type (NDA, MSA, SOW, Employment, etc.), extracts parties, dates, governing law. Your playbook standards are loaded automatically.",
    image: "/images/features-analysis.webp",
  },
  {
    num: "03",
    title: "Analyze",
    body: "Every clause rated RED, YELLOW, or GREEN against market standards and your firm\u2019s playbook. Deviation analysis, business impact, and specific redline suggestions for each clause.",
    image: "/images/in-house-compliance.webp",
  },
  {
    num: "04",
    title: "Review",
    body: "Attorney reviews each finding. For flagged clauses: Address it (AI drafts revised language), Accept it as-is, or Skip for later. Every decision is deliberate.",
    image: "/images/how-it-works-review.webp",
  },
  {
    num: "05",
    title: "Generate",
    body: "Click one button. Download a Word document with redlined revisions \u2014 original text struck through, revisions underlined. The format opposing counsel expects.",
    image: "/images/features-obligations.webp",
  },
  {
    num: "06",
    title: "Track Obligations",
    body: "Deadlines, responsible parties, recurring obligations extracted automatically. Export to Outlook or Google Calendar. Custom obligations with date picker. CSV export for compliance.",
    image: "/images/in-house-gc.webp",
  },
  {
    num: "07",
    title: "Upload Revision",
    body: "Opposing counsel sends back a revised version. Upload it. The system runs a full-document text diff \u2014 not AI comparison. Red strikethrough for deletions, green underline for additions.",
    image: "/images/features-versioning.webp",
  },
  {
    num: "08",
    title: "Intelligent Carry-Forward",
    body: "The AI doesn\u2019t start over. It checks each existing clause against the diff. Unchanged analysis carries forward exactly. Only impacted clauses get re-evaluated. Your obligations, executive summary, negotiation strategy \u2014 all preserved unless the change warrants an update.",
    image: "/images/security-enterprise.webp",
  },
  {
    num: "09",
    title: "Ethical Walls & Access Control",
    badge: "Enterprise",
    body: "Assign ethical walls to matters for ABA Model Rule 1.10 compliance. Information barriers use deny-overrides-allow enforcement \u2014 wall members are blocked from walled projects regardless of other access grants. Full audit trail. OIDC SSO, SCIM provisioning, and group-based project access round out enterprise identity management.",
    image: "/images/security-hero.webp",
  },
];

export default function HowItWorksPage() {
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll("[data-animate]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const show = (id: string) => visibleSections.has(id);

  return (
    <div
      className="min-h-screen bg-white text-gray-900"
      style={{ fontFamily: "'Georgia', 'Times New Roman', serif", scrollBehavior: "smooth" }}
    >
      <style>{`html { scroll-behavior: smooth; }`}</style>

      {/* ─── NAV ─── */}
      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          scrollY > 40 ? "bg-white shadow-md py-1" : "bg-white/90 py-1.5"
        }`}
      >
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <Link href="/" className="flex items-center cursor-pointer">
            <img src="/logo.png" alt="Contract Lucidity" className="h-12 md:h-14" />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link href="/features" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Features</Link>
            <Link href="/how-it-works" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>How It Works</Link>
            <Link href="/law-firms" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Law Firms</Link>
            <Link href="/in-house" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>In-House</Link>
            <Link href="/security" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Security</Link>
            <Link href="/why-contract-lucidity" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Why Us</Link>
            <a href="https://lucidvault.contractlucidity.com" target="_blank" rel="noopener noreferrer" className="text-sm text-[#C8A84B] hover:text-[#b8993f] transition-colors font-semibold" style={{ fontFamily: "system-ui, sans-serif" }}>Lucid Vault</a>
            <Link
              href="/login"
              className="px-6 py-2.5 text-sm font-semibold bg-[#1B3A6B] text-white rounded hover:bg-[#2E5EA0] transition-colors"
              style={{ fontFamily: "system-ui, sans-serif" }}
            >
              Sign In
            </Link>
          </div>
          {/* Mobile hamburger */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2" aria-label="Toggle menu">
            <svg className="w-6 h-6 text-[#1B3A6B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              }
            </svg>
          </button>
        </div>
        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg" style={{ fontFamily: "system-ui, sans-serif" }}>
            <div className="flex flex-col px-6 py-4 gap-3">
              <Link href="/features" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Features</Link>
              <Link href="/how-it-works" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">How It Works</Link>
              <Link href="/law-firms" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Law Firms</Link>
              <Link href="/in-house" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">In-House</Link>
              <Link href="/security" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Security</Link>
              <Link href="/why-contract-lucidity" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Why Us</Link>
              <a href="https://lucidvault.contractlucidity.com" target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)} className="text-sm text-[#C8A84B] font-semibold hover:text-[#b8993f] py-2 border-b border-gray-50">Lucid Vault</a>
              <Link href="/login" onClick={() => setMobileMenuOpen(false)} className="text-sm font-semibold text-white bg-[#1B3A6B] rounded px-4 py-2.5 text-center mt-2">Sign In</Link>
            </div>
          </div>
        )}
      </nav>

      {/* ─── HERO ─── */}
      <section className="pt-32 pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <p
              className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-6"
              style={{ fontFamily: "system-ui, sans-serif" }}
            >
              The Complete Contract Review Lifecycle
            </p>
            <h1 className="text-5xl md:text-6xl leading-[1.15] mb-6 text-[#1B3A6B]">
              From upload to action.<br />
              <span className="text-[#C8A84B]">Every step, your attorney in control.</span>
            </h1>
            <p
              className="text-lg text-gray-500 leading-relaxed mb-10 max-w-2xl mx-auto"
              style={{ fontFamily: "system-ui, sans-serif" }}
            >
              The complete contract review lifecycle in one platform.
            </p>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-6 mt-8">
          <div className="rounded-xl overflow-hidden shadow-xl border border-gray-100">
            <img
              src="/images/how-it-works-hero.webp"
              alt="Contract review lifecycle overview"
              className="w-full h-64 md:h-96 object-cover"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* ─── TIMELINE ─── */}
      <section className="py-24 bg-[#F8F9FB]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="relative">
            {/* Vertical line */}
            <div
              className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gray-200 -translate-x-1/2"
              aria-hidden="true"
            />

            <div className="space-y-20 md:space-y-24">
              {steps.map((step, i) => {
                const id = `step-${step.num}`;
                const isLeft = i % 2 === 0;

                return (
                  <div
                    key={step.num}
                    id={id}
                    data-animate
                    className={`relative transition-all duration-700 ${
                      show(id) ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                    }`}
                  >
                    {/* Dot on timeline */}
                    <div
                      className="hidden md:flex absolute left-1/2 -translate-x-1/2 top-2 w-12 h-12 rounded-full bg-[#1B3A6B] text-white items-center justify-center text-sm font-bold z-10"
                      style={{ fontFamily: "system-ui, sans-serif" }}
                    >
                      {step.num}
                    </div>

                    {/* Content */}
                    <div
                      className={`md:w-[45%] ${
                        isLeft ? "md:mr-auto md:pr-12" : "md:ml-auto md:pl-12"
                      }`}
                    >
                      <div className="bg-white rounded-lg p-8 border border-gray-100 shadow-sm">
                        {/* Mobile step number */}
                        <div
                          className="md:hidden flex items-center gap-3 mb-4"
                        >
                          <span
                            className="flex w-10 h-10 rounded-full bg-[#1B3A6B] text-white items-center justify-center text-sm font-bold shrink-0"
                            style={{ fontFamily: "system-ui, sans-serif" }}
                          >
                            {step.num}
                          </span>
                          <h3 className="text-xl text-[#1B3A6B]">
                            {step.title}
                            {(step as any).badge && <span className="ml-2 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-amber-100 text-amber-700 align-middle">{(step as any).badge}</span>}
                          </h3>
                        </div>

                        <h3 className="hidden md:block text-xl text-[#1B3A6B] mb-3">
                          {step.title}
                          {(step as any).badge && <span className="ml-2 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-amber-100 text-amber-700 align-middle">{(step as any).badge}</span>}
                        </h3>
                        <p
                          className="text-sm text-gray-500 leading-relaxed"
                          style={{ fontFamily: "system-ui, sans-serif" }}
                        >
                          {step.body}
                        </p>

                        {step.image && (
                          <div className="mt-6 rounded-lg overflow-hidden border border-gray-100">
                            <img
                              src={step.image}
                              alt={`${step.title} screenshot`}
                              className="w-full h-48 object-cover"
                              loading="lazy"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ─── LUCID VAULT CALLOUT ─── */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-[#F8F9FB] rounded-xl p-8 md:p-10 border border-gray-100 flex flex-col md:flex-row items-center gap-8">
            <div className="shrink-0">
              <span className="w-14 h-14 rounded-full bg-[#C8A84B] text-white flex items-center justify-center text-lg font-bold" style={{ fontFamily: "system-ui, sans-serif" }}>LV</span>
            </div>
            <div className="flex-1">
              <h3 className="text-xl text-[#1B3A6B] mb-2">Need a secure way to collect contracts from clients?</h3>
              <p className="text-sm text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>
                Lucid Vault provides encrypted, white-labeled document exchange — so your clients can send contracts securely before you analyze them in Contract Lucidity.
              </p>
            </div>
            <a
              href="https://lucidvault.contractlucidity.com"
              rel="noopener noreferrer"
              className="shrink-0 px-6 py-3 text-sm font-semibold bg-[#C8A84B] text-white rounded hover:bg-[#b8993f] transition-colors whitespace-nowrap"
              style={{ fontFamily: "system-ui, sans-serif" }}
            >
              Explore Lucid Vault
            </a>
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section id="cta" data-animate className="py-24 bg-[#C8A84B]">
        <div className="max-w-3xl mx-auto px-6">
          <div
            className={`transition-all duration-700 ${
              show("cta") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <div className="bg-white rounded-xl border-2 border-[#1B3A6B] p-12 text-center shadow-lg">
              <h2 className="text-3xl md:text-4xl text-[#1B3A6B] mb-4">
                Ready to see it in action?
              </h2>
              <p
                className="text-gray-500 mb-10 max-w-xl mx-auto"
                style={{ fontFamily: "system-ui, sans-serif" }}
              >
                Upload a real contract. See the full lifecycle from analysis to tracked-changes
                document. All in under 5 minutes.
              </p>
              <a
                href="/demo"
                className="inline-block px-10 py-4 text-sm font-semibold bg-[#1B3A6B] text-white rounded hover:bg-white hover:text-[#1B3A6B] hover:ring-2 hover:ring-[#1B3A6B] transition-all"
                style={{ fontFamily: "system-ui, sans-serif" }}
              >
                Request a Demo
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
